import { useEffect, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { useMutation } from "@apollo/client";
import type { DateValueType } from "react-tailwindcss-datepicker";
import { DateTime } from "luxon";
import Button from "@/components/Button";
import DateSelect from "@/components/DateSelect";
import Dialog from "@/components/Dialog";
import Select from "@/components/Select";
import TextField from "@/components/TextField";
import { showErrorSnackbar, showSnackbar } from "@/lib/snackbarUtils";
import {
  buildReasonCodeSelectOptions,
  getAllowedReasonCodes,
  getRequireTypeLabel
} from "@/features/person/utils/education-requirement-reason-codes";
import type { PgmProgramRequirement } from "@/types";
import {
  PGM_PROGRAM_TABLE_REFETCH_QUERIES,
  UPDATE_EDUCATION_PROGRAM_REQUIREMENT_MUTATION,
  type UpdateEducationProgramRequirementVariables
} from "./mutations";

type FormValues = {
  completionDate: string;
  completionStatus: string;
  score: string;
  remarks: string;
};

interface EditEducationRequirementModalProps {
  levelStartDate: string;
  onClose(): void;
  open: boolean;
  pgaId: string;
  requirement: PgmProgramRequirement;
}

function buildDefaultValues(requirement: PgmProgramRequirement): FormValues {
  return {
    completionDate: requirement.completionDate || "",
    completionStatus: requirement.completionStatus || "",
    score: requirement.score != null ? String(requirement.score) : "",
    remarks: requirement.remarks || ""
  };
}

function hasText(value: string | null | undefined) {
  return Boolean(value?.trim());
}

function validateCompletionPair(completionDate: string, completionStatus: string) {
  const hasDate = hasText(completionDate);
  const hasStatus = hasText(completionStatus);

  if (hasDate === hasStatus) {
    return true;
  }

  if (hasDate) {
    return "Choose a reason code, or clear the completion date too.";
  }

  return "Choose a completion date, or clear the reason code too.";
}

function buildMutationInput({
  pgaId,
  parsedScore,
  requirement,
  values
}: {
  pgaId: string;
  parsedScore: number | null;
  requirement: PgmProgramRequirement;
  values: FormValues;
}): UpdateEducationProgramRequirementVariables["input"] {
  return {
    pgaId,
    requireId: requirement.requireId,
    levelCode: requirement.levelCode,
    completionDate: values.completionDate || null,
    completionStatus: values.completionStatus || null,
    ...(parsedScore != null ? { score: parsedScore } : {}),
    ...(values.remarks.trim() ? { remarks: values.remarks } : {})
  };
}

export default function EditEducationRequirementModal({
  levelStartDate,
  onClose,
  open,
  pgaId,
  requirement
}: EditEducationRequirementModalProps) {
  const [updateRequirement, { loading: saving }] = useMutation(UPDATE_EDUCATION_PROGRAM_REQUIREMENT_MUTATION, {
    refetchQueries: PGM_PROGRAM_TABLE_REFETCH_QUERIES,
    awaitRefetchQueries: true
  });

  const { control, handleSubmit, reset, formState, trigger } = useForm<FormValues>({
    defaultValues: buildDefaultValues(requirement)
  });

  useEffect(() => {
    if (!open) {
      return;
    }

    reset(buildDefaultValues(requirement));
  }, [open, requirement, reset]);

  const reasonCodeOptions = buildReasonCodeSelectOptions(
    getAllowedReasonCodes(requirement.requirementDefinition?.requireType)
  );

  const minCompletionDate = useMemo(() => {
    if (!levelStartDate) {
      return undefined;
    }

    return DateTime.fromISO(levelStartDate).toJSDate();
  }, [levelStartDate]);

  const handleClose = () => {
    reset(buildDefaultValues(requirement));
    onClose();
  };

  const onSubmit = async (values: FormValues) => {
    const parsedScore = values.score.trim() === "" ? null : Number(values.score);

    try {
      const { data } = await updateRequirement({
        variables: {
          input: buildMutationInput({ pgaId, requirement, values, parsedScore })
        }
      });

      const payload = data?.updateEducationProgramRequirement;

      if (!payload?.success) {
        showErrorSnackbar(payload?.message || "Unable to update education requirement.");
        return;
      }

      showSnackbar("Education requirement updated");
      handleClose();
    } catch {
      showErrorSnackbar("Unable to update education requirement.");
    }
  };

  if (!open) {
    return null;
  }

  const requireTypeLabel = getRequireTypeLabel(requirement.requirementDefinition?.requireType, requirement.levelCode);

  return (
    <Dialog
      open={open}
      onClosed={handleClose}
      headline="Edit Education Requirement"
      className="w-[720px] h-fit max-h-[90vh] ![margin:8vh_auto_auto]"
      hasFormElements
      actions={
        <>
          <Button variant="text" onClick={handleClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSubmit(onSubmit)} disabled={saving || formState.isSubmitting}>
            Save
          </Button>
        </>
      }
    >
      <div className="space-y-6">
        <input type="hidden" name="requireId" value={requirement.requireId} />
        <TextField label="Requirement ID" value={requirement.requireId} disabled className="w-full" />
        <TextField label="Type" value={requireTypeLabel} disabled className="w-full" />
        <TextField
          label="Description"
          value={requirement.requirementDefinition?.description || "--"}
          disabled
          className="w-full"
        />

        <Controller
          name="completionDate"
          control={control}
          rules={{
            validate: {
              pairedWithReason: (value, values) => validateCompletionPair(value, values.completionStatus),
              onOrAfterLevelStart: (value) => {
                if (!value || !levelStartDate) {
                  return true;
                }

                const selectedDate = DateTime.fromISO(value).startOf("day");
                const startDate = DateTime.fromISO(levelStartDate).startOf("day");

                if (selectedDate < startDate) {
                  return "Completion date must be on or after the level start date";
                }

                return true;
              }
            }
          }}
          render={({ field: { onChange, value, onBlur }, fieldState: { error } }) => (
            <DateSelect
              label="Completion Date"
              value={{ startDate: value || null, endDate: value || null }}
              onChange={(dateValue: DateValueType | string | null) => {
                if (typeof dateValue === "string") return;
                onChange(dateValue?.startDate ?? "");
                void trigger("completionStatus");
              }}
              onBlur={onBlur}
              useSingleDate
              useMultiCalendar={false}
              useModal
              minDate={minCompletionDate}
              allowClear
              error={!!error}
              errorMessage={error?.message}
              data-testid="edit-education-requirement-completion-date"
            />
          )}
        />

        <Controller
          name="completionStatus"
          control={control}
          rules={{
            validate: (value, values) => validateCompletionPair(values.completionDate, value)
          }}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <Select
              label="Reason Code"
              value={value}
              options={reasonCodeOptions}
              onSelect={(nextValue) => {
                onChange(nextValue);
                void trigger("completionDate");
              }}
              error={!!error}
              errorText={error?.message}
              data-testid="edit-education-requirement-reason-code"
            />
          )}
        />

        <Controller
          name="score"
          control={control}
          rules={{
            validate: (value) => {
              if (requirement.score != null && value.trim() === "") {
                return "Score cannot be cleared";
              }

              if (value.trim() !== "" && Number.isNaN(Number(value))) {
                return "Score must be a number.";
              }

              return true;
            }
          }}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <TextField
              label="Score"
              value={value}
              onChangeText={onChange}
              className="w-full"
              error={!!error}
              errorText={error?.message}
            />
          )}
        />

        <Controller
          name="remarks"
          control={control}
          rules={{
            maxLength: {
              value: 60,
              message: "Remarks must be 60 characters or fewer"
            },
            validate: (value) => {
              if (hasText(requirement.remarks) && !value.trim()) {
                return "Remarks cannot be cleared";
              }

              return true;
            }
          }}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <TextField
              type="textarea"
              rows={3}
              label="Remarks"
              value={value}
              onChangeText={onChange}
              className="w-full"
              error={!!error}
              errorText={error?.message}
            />
          )}
        />
      </div>
    </Dialog>
  );
}
